import { get } from "svelte/store";
import { getPlanQueryParameter, mapModeStore } from "../map-mode-store";
import { toggleSideMenuOption } from "$lib/utils/ui-utils";
import { toggleEscapeToolListener } from "../map-tools";
import { busyStateMessage, useCesiumViewer } from "../cesium-viewer";
import { handleUnauthorizedResourceFetch, useDataSources } from "../cesium-datasources";
import { mapToolsStore } from "../map-tools-store";
import { Matrix4, Transforms, Cartesian3, Entity, EntityCollection, ColorMaterialProperty, ConstantProperty, PolylineOutlineMaterialProperty, Color, ConstantPositionProperty, CallbackPositionProperty, Cartesian2, defined, Cartographic, RequestErrorEvent, ScreenSpaceEventType, sampleTerrainMostDetailed, Viewer, CzmlDataSource } from "cesium";
import { ENTITY_ID_PREFIX, SideMenuOptions } from "$lib/utils/constants";
import { cartesianToLatitudeLongitude, getEntityPropertyBag, handleContextualizedCzmlResponse, setSelectedEntity } from "../map-utils";
import type { IMastPointMinimalModel } from "$lib/models/IMastPointMinimalModel";
import { HTTPError } from "ky";
import api from "$lib/services/kyAuthenticatedInstance";
import { PoleAndLineService } from "$lib/services/pole-and-line-service";
import type { ContextualizedCzmlResponse, MastPointUpdateResponse } from "$lib/models/IProjectModel";
import { toast } from "$lib/components/organisms/toast/toast";
import { errorDetailsExtractor } from "$lib/utils/error-details-extractor";
import { OriginalCesiumInputActions } from "../cesium-original-input-actions";
import { collectGroupEntities, createPreviewEntity, getGroupRoot, makeEnuFrameAt } from "./utils";
import type { IMastpointDataModel, IMastPointPutModel } from "$lib/models/IMastPointFullModel";
import { llToUtm, utmToLl } from "$lib/utils/coordinate-converter";
import { useMutation } from "@sveltestack/svelte-query";
import { mutations } from "$lib/data/mutations";
import { confirmModal } from "$lib/components/organisms/confirmModal/confirmModal";

type MoveSession = {
    root: Entity;
    children: Entity[];
    previewRoot: Entity;
    previewChildren: {
        child: Entity;
        previewChild: Entity;
        localOffset: Cartesian3;
    }[];
    startRootPos: Cartesian3;
};


/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////         TOGGLE TOOL FUNCTION         ////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * As opposed to other tools in toolbar, this function is triggered from several locations in the ui.
 * Trigger the toggle to either enable or disable new pole creation. If enabled, attach relevant (mouse) event listeners to Cesium.
 */
export const useAdjustPositionTool = (mastId?: number) => {
    if (get(mapModeStore).selectedProjectAndPlan.plan.id === undefined) {
        toast({
            title: 'Ingen plan valgt',
            type: 'warning',
            description: 'Vennligst velg en plan for å redigere mastepunkt.'
        });
        return;
    }

    toggleSideMenuOption(SideMenuOptions.NONE, false);
    //  toggleMoveMastPointEntityInMap(mastpoint) is the OLD function which should trigger on old entities
    toggleQuickMoveMastpointPosition(mastId);
    
};

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////         SEPARATION OF CONCERNS         ///////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * A function that handles picking a world position from a screen position.
 * @param screenPos X/Y screen position of click event
 * @param viewer The Cesium Viewer instance
 * @returns Cartesian3
 */
async function pickWorldPosition(screenPos: Cartesian2, viewer: Viewer) {
    const scene = viewer.scene;
    let cartesian: Cartesian3 | undefined;

    // Depth picking 3D Tiles / models / terrain
    if (scene.pickPositionSupported) {
        const p = scene.pickPosition(screenPos);
        if (defined(p)) {
            cartesian = p;
        }
    }

    // Fallback: ellipsoid / globe pick
    if (!cartesian) {
        const ray = viewer.camera.getPickRay(screenPos);
        if (!ray) return undefined;

        cartesian = scene.globe.pick(ray, scene);
        if (!cartesian) return undefined;
    }

    // Convert to Cartographic
    const cartographic = Cartographic.fromCartesian(cartesian);
    if (!cartographic) return undefined;

    // Sample terrain 
    const [sampled] = await sampleTerrainMostDetailed(
        viewer.terrainProvider,
        [cartographic]
    );

    // Convert back to Cartesian at terrain height
    return Cartesian3.fromRadians(
        sampled.longitude,
        sampled.latitude,
        sampled.height
    );
}

/**
 * Computes the position of child entities relative to the local ENU frame of the parent
 * @param parentFrameInv Local ENU frame inverted (Matrix4)
 * @param childWorldPos World coordinates of child Entity (Cartesian3)
 * @returns Cartesian3 in local coordinates
 */
function computeLocalOffset(parentFrameInv: Matrix4, childWorldPos: Cartesian3) {
    return Matrix4.multiplyByPoint(parentFrameInv, childWorldPos, new Cartesian3());
}

/**
 * When an entity is selected, information about the entity group is collected and stored in a session object. This also indicates that we have selected a group and can start moving.
 * @param pickedChildEntity Entity
 * @param refDatasource Datasource to use for preview entities
 * @returns session object or null if failed
 */
function startMovePreview(pickedChildEntity: Entity, refDatasource: CzmlDataSource, group: 'full' | 'poleConstruction' | 'singlePole' = 'full', rootOverride?: Cartesian3, groupOverride?: Entity[]) {

    let root = pickedChildEntity;
    
    if(group === 'full'){
        root = getGroupRoot(pickedChildEntity);
    } else if (group === 'poleConstruction') {
        // Construct artificial root for pole construction. The group has it's own coordinates, but not Entity
        root = new Entity({
            point: {
                color: new ConstantProperty(Color.YELLOW),
                pixelSize: new ConstantProperty(4),
                disableDepthTestDistance: new ConstantProperty(null),
                scaleByDistance: new ConstantProperty(null),
            }
        })
    }
    const rootPos = rootOverride ?? root.position?.getValue();
    if (!rootPos) return null;

    const children = groupOverride ?? collectGroupEntities(root, root.entityCollection);

    const rootFrame = makeEnuFrameAt(rootPos);
    const rootFrameInv = Matrix4.inverse(rootFrame, new Matrix4());

    const previewRoot = createPreviewEntity(root, refDatasource)
    
    if(previewRoot?.cylinder) {
        // Fix magnitude of cylinder. Move Z up by half height to align with original mastpoint entity
        // This is ONLY needed for mastpoints with cylinder graphics
        previewRoot!.position = new CallbackPositionProperty((() => {
            const originalHeight = root.cylinder?.length?.getValue() ?? 0;
            const cylinderMagnitude = Cartesian3.magnitude(rootPos)
            const newMagnitude = cylinderMagnitude + originalHeight/2;
            const scalar = newMagnitude / cylinderMagnitude;

            return Cartesian3.multiplyByScalar(rootPos, scalar, new Cartesian3());
        }), false);
    } else {
        previewRoot!.position = new CallbackPositionProperty((() => rootPos), false);
    }

    const previewChildren = [];

    for (const child of children) {
        const childPos = child.position?.getValue();
        if (!childPos) continue;

        const localOffset = computeLocalOffset(rootFrameInv, childPos);

        const previewChild = createPreviewEntity(child, refDatasource);
        if(!previewChild) {
            previewChildren.push(undefined);
            continue;
        };
        previewChild.position = new CallbackPositionProperty((() => childPos), false);

        previewChildren.push({ child, previewChild, localOffset });
    }

    return {
        root,
        children,
        previewRoot,
        previewChildren: previewChildren.filter(pc => pc !== undefined),
        startRootPos: Cartesian3.clone(rootPos),
    };
}

/**
 * Moves the ENU frame to the new root position, and updates all child positions accordingly.
 * @param session 
 * @param newRootPos 
 * @returns void
 */
function updateMovePreview(session: MoveSession, newRootPos: Cartesian3) {
    if (!session) return;

    const rootFrame = makeEnuFrameAt(newRootPos);

    if(session.previewRoot?.cylinder) {
        // Fix magnitude of cylinder. Move Z up by half height to align with original mastpoint entity
        // This is ONLY needed for mastpoints with cylinder graphics
        session.previewRoot!.position = new CallbackPositionProperty((() => {
            const originalHeight = session.previewRoot.cylinder?.length?.getValue() ?? 0;
            const cylinderMagnitude = Cartesian3.magnitude(newRootPos)
            const newMagnitude = cylinderMagnitude + originalHeight/2;
            const scalar = newMagnitude / cylinderMagnitude;

            return Cartesian3.multiplyByScalar(newRootPos, scalar, new Cartesian3());
        }), false);
    } else {
        session.previewRoot.position = new CallbackPositionProperty((() => newRootPos), false);
    }

    

    for (const item of session.previewChildren) {
        const childWorld = Matrix4.multiplyByPoint(
            rootFrame,
            item.localOffset,
            new Cartesian3()
        );
        item.previewChild.position  = new CallbackPositionProperty((() => childWorld), false);
    }
}

/**
 * The function handling the HTTP request when a position is commited.
 * @param payload 
 * @param mastpointPropertyBag 
 */
const updatePosition = async (payload: {id: number, lat: number, lng: number}, mastpointPropertyBag: IMastPointMinimalModel.MastPointPropertyBagModel["data"]) => {
    const { temp_referenceDataSource } = useDataSources();

    // Endpoint to check if there are any connected sections and if they can be moved
    const czmlQueryParameters = get(getPlanQueryParameter);
    const data = await api.get<{canMoveConnectedSections: boolean}>(`MastPoint/UpdatePosition/${payload.id}/options${czmlQueryParameters}`).json();

    // The function to handle the actual move operation
    const handleMoveMastPoint = async (moveConnectedSections: boolean) => {
        const mastPointPositionUpdateResponse = await PoleAndLineService.updateMastPointPosition({
            updatedMastPoint: payload,
            moveConnectedSections
        });

        const mastPointCZML: ContextualizedCzmlResponse = await mastPointPositionUpdateResponse.json();
        await handleContextualizedCzmlResponse(mastPointCZML);

        toast({
            type: 'success',
            title: 'Mastepunkt oppdatert',
            description: `Mastepunkt <b>${mastpointPropertyBag.externalMastId}</b> sin posisjon ble oppdatert. Du kan fortsette eller avslutte verktøyet.`
        });
        temp_referenceDataSource.entities.removeAll();
    }

    // When the endpoint indicates that there are connected sections which can be moved, prompt the user for confirmation
    if(data && data.hasOwnProperty('canMoveConnectedSections') && data.canMoveConnectedSections === true){
        confirmModal(
            `Du endrer traséen ved å flytte denne masten. Dette påvirker tilhørende bæremaster. Vil du flytte bæremaster inn på ny trasé?`, 
            async () => {
                // User confirmed moving connected sections
                await handleMoveMastPoint(true);
            }, async () => {
                // User denied moving connected sections
                await handleMoveMastPoint(false);
            }, {
                labels: {
                    confirm: 'Flytt bæremaster',
                    cancel: 'La bæremaster stå',
                    title: 'Vil du flytte bæremaster?'
                }
            }
        )
    }else {
        await handleMoveMastPoint(false);
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////         TOOL HANDLER         ////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const toggleQuickMoveMastpointPosition = async (mastId?: number) => {
    const viewer = useCesiumViewer();
    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
    const { temp_referenceDataSource, mapdataDataSource } = useDataSources();
    mapToolsStore.disableAllToolsExceptToggleToolByName('quickMoveMastPoint', viewer);

    let moving: boolean = false;
    let session: MoveSession | null = null;

    const killswitch = () =>{
        session = null;
        moving = false;
        temp_referenceDataSource.entities.removeAll();
    }
    
    toggleEscapeToolListener({toggleOn: false});

    // Activate tool
    if(get(mapToolsStore).quickMoveMastPoint.toolEnabled){
        // If mastpointId is provided on initialization - start move session directly.
        if(defined(mastId)){
            const planDatasource = get(mapModeStore).activeDataSource;

            let mastpointEntity = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.MASTPOINT + mastId) as Entity | undefined;
            if(!mastpointEntity && planDatasource){
                mastpointEntity = planDatasource.entities.getById(ENTITY_ID_PREFIX.MASTPOINT + mastId) as Entity | undefined;
            }
            if(!mastpointEntity) return;
            if(!viewer.dataSources.contains(temp_referenceDataSource)){
                viewer.dataSources.add(temp_referenceDataSource);
            }
            // @ts-expect-error Function is defined to never return undefined for root
            session = startMovePreview(mastpointEntity, temp_referenceDataSource);
            if (!session) {
                console.error(`Could not start move session for mastpoint with id ${mastId}`);
                return;
            }
            moving = true;
        }

        screenSpaceEventHandler.setInputAction(async (event: { position: Cartesian2 }) => {
            if (!moving && !session) {
                const picked = viewer.scene.pick(event.position);
                const pickedEntity = picked?.id as Entity | undefined;
                let mastpointEntity: Entity | undefined = undefined;

                if (!picked || !defined(pickedEntity)) return;

                if (pickedEntity.id.startsWith(ENTITY_ID_PREFIX.CROSSBEAM) || pickedEntity.id.startsWith(ENTITY_ID_PREFIX.POLE) || pickedEntity.id.startsWith(ENTITY_ID_PREFIX.UTILITYLINK) ) {
                    mastpointEntity = pickedEntity.parent;
                } else if (pickedEntity.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) {
                    mastpointEntity = pickedEntity;
                }
                if(!mastpointEntity) return;

                if(!viewer.dataSources.contains(temp_referenceDataSource)){
                    viewer.dataSources.add(temp_referenceDataSource);
                }

                // @ts-expect-error Function is defined to never return undefined for root
                session = startMovePreview(mastpointEntity, temp_referenceDataSource);
                if (!session)  return;

                moving = true;
                return;
            } 
            
            if(defined(session) && moving){
                // Commit
                busyStateMessage.set('Flytter mastepunkt. Vennligst vent...');

                const finalRootPos = await pickWorldPosition(event.position, viewer); // session.previewRoot.position.getValue(); Can't use yet as we sometimes use a cylinder for root, which manipulates position
                const startRootPos = session.startRootPos; // Used for delta 
               
                if(!finalRootPos) return;
                const {latitude, longitude} = cartesianToLatitudeLongitude(finalRootPos);

                // We may show the distance moved by using: const delta = Cartesian3.subtract(finalRootPos, startRootPos, new Cartesian3());
                const mastpointPropertyBag = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(session.root).data;

                const payload = {
                    id: mastpointPropertyBag.id,
                    lat: latitude,
                    lng: longitude
                }

                try {
                    await updatePosition(payload, mastpointPropertyBag);
                    session = null;
                    moving = false;

                } catch (error: unknown) {
                    busyStateMessage.set('');
                    killswitch();

                    if (error instanceof RequestErrorEvent) {
                        handleUnauthorizedResourceFetch(error);
                    } else if (error instanceof HTTPError) {
                        const errorDetails = await errorDetailsExtractor(error.response);
                        if (errorDetails) {
                            toast({
                                type: 'warning',
                                title: 'Feil ved oppdatering av mastepunkt',
                                description: `Feilmelding: ${errorDetails}`
                            });
                        } else {
                            toast({
                                type: 'warning',
                                title: 'Feil ved oppdatering av mastepunkt',
                                description: `En feil oppsto i forbindelse med oppdatering av mastepunkt-posisjon.`
                            });
                        }
                    } else {
                        console.error(error);
                        toast({
                            type: 'error',
                            title: 'Feil ved oppdatering av mastepunkt',
                            description: `En feil oppstod ved kontakt med server. Vennligst vent litt og prøv igjen.`
                        });
                    }
                }
                finally {
                    busyStateMessage.set('')
                }
            }
        }, ScreenSpaceEventType.LEFT_CLICK);

        screenSpaceEventHandler.setInputAction(async (movement: { endPosition: Cartesian2 }) => {
            if (!moving || !session) return;
            const p = await pickWorldPosition(movement.endPosition, viewer);
            if (!p) return;

            updateMovePreview(session, p);
        }, ScreenSpaceEventType.MOUSE_MOVE);

    // Deactivate tool
    } else {
        toggleEscapeToolListener({toggleOn: false});
        killswitch();
        // Reset the left click input action
        screenSpaceEventHandler.setInputAction(
            OriginalCesiumInputActions.LEFT_CLICK,
            ScreenSpaceEventType.LEFT_CLICK
        );
        screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
    }
}

/**
 * Quick tool and quick implementation. A clone of mastpoint tool which instead posts a single pole position
 * @param poleId 
 * @returns 
 */
export const toggleQuickMovePolePosition = async (mastId?: number, poleData?: IMastpointDataModel.Pole) => {
    const viewer = useCesiumViewer();
    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
    const { temp_referenceDataSource, mapdataDataSource } = useDataSources();
    mapToolsStore.disableAllToolsExceptToggleToolByName('quickMovePole', viewer);
    let moving: boolean = false;
    let session: MoveSession | null = null;

    const killswitch = () =>{
        session = null;
        moving = false;
        temp_referenceDataSource.entities.removeAll();
    }

    if(!poleData || !mastId){
        killswitch()
        return;
    }

    const poleId = poleData.id;

    toggleEscapeToolListener({toggleOn: false});

    // Activate tool
    if(get(mapToolsStore).quickMovePole.toolEnabled){
        // If mastpointId is provided on initialization - start move session directly.
        const planDatasource = get(mapModeStore).activeDataSource;

        let poleEntity = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
        if(!poleEntity && planDatasource){
            poleEntity = planDatasource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
        }
        if(!poleEntity) return;
        if(!viewer.dataSources.contains(temp_referenceDataSource)){
            viewer.dataSources.add(temp_referenceDataSource);
        }
        // @ts-expect-error Function is designed to never return undefined for root
        session = startMovePreview(poleEntity, temp_referenceDataSource, 'singlePole');
        if (!session) {
            console.error(`Could not start move session for pole with id ${poleId}`);
            return;
        }
        moving = true;

        screenSpaceEventHandler.setInputAction(async (event: { position: Cartesian2 }) => {
             
            if(defined(session) && moving){
                // Commit
                busyStateMessage.set('Flytter stolpe. Vennligst vent...');

                const finalRootPos = await pickWorldPosition(event.position, viewer); // session.previewRoot.position.getValue(); Can't use yet as we sometimes use a cylinder for root, which manipulates position
                const startRootPos = session.startRootPos; // Used for delta 
               
                if(!finalRootPos) return;
                const {latitude, longitude} = cartesianToLatitudeLongitude(finalRootPos);

                try{
                    const result = await api.put<MastPointUpdateResponse>(`MastPoint/UpdatePosition/poles/${poleId}/czml` + get(getPlanQueryParameter), { json: {
                        lat: latitude,
                        lng: longitude
                    }}).json();

                    await handleContextualizedCzmlResponse(result.czml);

                    poleEntity = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
                    if(!poleEntity && planDatasource){
                        poleEntity = planDatasource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
                    }
                    setSelectedEntity(poleEntity);
                } catch (error){
                    if (error instanceof RequestErrorEvent) {
                        handleUnauthorizedResourceFetch(error);
                    } else if (error instanceof HTTPError) {
                        const errorDetails = await errorDetailsExtractor(error.response);
                        if (errorDetails) {
                            toast({
                                type: 'warning',
                                title: 'Feil ved oppdatering av mastepunkt',
                                description: `Feilmelding: ${errorDetails}`
                            });
                        } else {
                            toast({
                                type: 'warning',
                                title: 'Feil ved oppdatering av mastepunkt',
                                description: `En feil oppsto i forbindelse med oppdatering av mastepunkt-posisjon.`
                            });
                        }
                    } else {
                        console.error(error);
                        toast({
                            type: 'error',
                            title: 'Feil ved oppdatering av mastepunkt',
                            description: `En feil oppstod ved kontakt med server. Vennligst vent litt og prøv igjen.`
                        });
                    }
                } finally {
                    killswitch();
                    busyStateMessage.set('');
                }

                session = null;
                moving = false;

            }
        }, ScreenSpaceEventType.LEFT_CLICK);

        screenSpaceEventHandler.setInputAction(async (movement: { endPosition: Cartesian2 }) => {
            if (!moving || !session) return;
            const p = await pickWorldPosition(movement.endPosition, viewer);
            if (!p) return;

            updateMovePreview(session, p);
        }, ScreenSpaceEventType.MOUSE_MOVE);

    // Deactivate tool
    } else {
        toggleEscapeToolListener({toggleOn: false});
        killswitch();
        // Reset the left click input action
        screenSpaceEventHandler.setInputAction(
            OriginalCesiumInputActions.LEFT_CLICK,
            ScreenSpaceEventType.LEFT_CLICK
        );
        screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
    }
}

/**
 * Quick tool and quick implementation. A clone of mastpoint tool which instead posts a single pole position
 * @param poleId 
 * @returns 
 */
export const toggleQuickMovePoleConstructionPosition = async (mastId?: number, poleConstructionData?: IMastPointPutModel.PoleConstructionPutData) => {
    const viewer = useCesiumViewer();
    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
    const { temp_referenceDataSource, mapdataDataSource } = useDataSources();
    mapToolsStore.disableAllToolsExceptToggleToolByName('quickMovePoleConstruction', viewer);
    let moving: boolean = false;
    let session: MoveSession | null = null;

    const killswitch = () =>{
        session = null;
        moving = false;
        temp_referenceDataSource.entities.removeAll();
    }

    if(!poleConstructionData || !mastId){
        killswitch()
        return;
    }

    const poleId = poleConstructionData.mainPoleId;

    toggleEscapeToolListener({toggleOn: false});

    // Activate tool
    if(get(mapToolsStore).quickMovePoleConstruction.toolEnabled){
        // If mastpointId is provided on initialization - start move session directly.
        const planDatasource = get(mapModeStore).activeDataSource;

        let poleEntity = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
        if(!poleEntity && planDatasource){
            poleEntity = planDatasource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
        }
        if(!poleEntity) return;
        if(!viewer.dataSources.contains(temp_referenceDataSource)){
            viewer.dataSources.add(temp_referenceDataSource);
        }

        const poleConstructionEntities = poleConstructionData.supportPoles?.map((e) => poleEntity!.entityCollection.getById(ENTITY_ID_PREFIX.POLE + e.id)).filter((e) => e !== undefined) ?? [];
        const poleGroup: Entity[] = [poleEntity, ...poleConstructionEntities];

        const poleConstructionRoot = utmToLl(poleConstructionData.position!.yCoordinate, poleConstructionData.position!.xCoordinate, 32, 'N');
        const terrainSample = await sampleTerrainMostDetailed(viewer.terrainProvider, [
            Cartographic.fromDegrees(poleConstructionRoot.lng, poleConstructionRoot.lat)
        ]);
        const poleConstructionRootCartesian = Cartesian3.fromDegrees(poleConstructionRoot.lng, poleConstructionRoot.lat, terrainSample[0].height);

        // rootPos is wrong in this example. Test further. terrain sample or conversion may give weird results.
        // @ts-expect-error Function is defined to never return undefined for root
        session = startMovePreview(poleEntity, temp_referenceDataSource, 'poleConstruction', poleConstructionRootCartesian, poleGroup);
        if (!session) {
            console.error(`Could not start move session for pole with id ${poleId}`);
            return;
        }
        moving = true;

        screenSpaceEventHandler.setInputAction(async (event: { position: Cartesian2 }) => {
             
            if(defined(session) && moving){
                // Commit
                busyStateMessage.set('Flytter stolpekonstruksjon. Vennligst vent...');

                const finalRootPos = await pickWorldPosition(event.position, viewer); // session.previewRoot.position.getValue(); Can't use yet as we sometimes use a cylinder for root, which manipulates position
                const startRootPos = session.startRootPos; // Used for delta 
               
                if(!finalRootPos) return;
                const {latitude, longitude} = cartesianToLatitudeLongitude(finalRootPos);
                const utm = llToUtm(latitude, longitude);

                try{
                    const result: MastPointUpdateResponse = await api.put(`MastPoint/${mastId}/czml` + get(getPlanQueryParameter), { json: {
                        polesConstruction: {
                        ...poleConstructionData,
                        position: {
                            xCoordinate: utm.northing,
                            yCoordinate: utm.easting
                        }
                    }}}).json();

                    await handleContextualizedCzmlResponse(result.czml);

                    poleEntity = mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
                    if(!poleEntity && planDatasource){
                        poleEntity = planDatasource.entities.getById(ENTITY_ID_PREFIX.POLE + poleId) as Entity | undefined;
                    }

                    // Toggle off. This function only happens by using the mastpoint context menu. No need for further selection.
                    await toggleQuickMovePoleConstructionPosition(mastId, poleConstructionData);
                    setSelectedEntity(poleEntity);

                } catch (error){
                    await toggleQuickMovePoleConstructionPosition(mastId, poleConstructionData);
                    if (error instanceof RequestErrorEvent) {
                        handleUnauthorizedResourceFetch(error);
                    } else if (error instanceof HTTPError) {
                        const errorDetails = await errorDetailsExtractor(error.response);
                        if (errorDetails) {
                            toast({
                                type: 'warning',
                                title: 'Feil ved oppdatering av stolpekonstruksjon',
                                description: `Feilmelding: ${errorDetails}`
                            });
                        } else {
                            toast({
                                type: 'warning',
                                title: 'Feil ved oppdatering av stolpekonstruksjon',
                                description: `En feil oppsto i forbindelse med oppdatering av stolpekonstruksjonens posisjon.`
                            });
                        }
                    } else {
                        toast({
                            type: 'error',
                            title: 'Feil ved oppdatering av stolpekonstruksjon',
                            description: `En feil oppstod ved kontakt med server. Vennligst vent litt og prøv igjen.`
                        });
                    }
                } finally {
                    killswitch();
                    busyStateMessage.set('');
                }

                session = null;
                moving = false;

            }
        }, ScreenSpaceEventType.LEFT_CLICK);

        screenSpaceEventHandler.setInputAction(async (movement: { endPosition: Cartesian2 }) => {
            if (!moving || !session) return;
            const p = await pickWorldPosition(movement.endPosition, viewer);
            if (!p) return;

            updateMovePreview(session, p);
        }, ScreenSpaceEventType.MOUSE_MOVE);

    // Deactivate tool
    } else {
        toggleEscapeToolListener({toggleOn: false});
        killswitch();
        // Reset the left click input action
        screenSpaceEventHandler.setInputAction(
            OriginalCesiumInputActions.LEFT_CLICK,
            ScreenSpaceEventType.LEFT_CLICK
        );
        screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
    }
}